"use client"

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react"
const TILE_CSS_EASE = "cubic-bezier(0.16, 1, 0.3, 1)"

/**
 * Static positioned wrapper for a floating hero tile.
 *
 * Layout:
 * - X is a percentage of the hero content area's width (referenced to
 *   the 1440px desktop) so tiles scale inward as the viewport shrinks
 *   without overflowing past the cyclist photo.
 * - Y is a fixed pixel offset from the top of the hero content area.
 * - Hidden below `lg` (1024px) — the composition relies on the tiles
 *   flanking the cyclist with enough breathing room. At tablet widths
 *   the cyclist takes the centre column and there isn't room for tiles
 *   on either side without overlapping her, so we drop them entirely.
 *
 * Entrance:
 * - One-shot blur-to-focus with a slight upward translate, staggered
 *   per tile via `delay`. No continuous loop — the previous Y bob
 *   transformed by sub-pixel amounts and rendered without a dedicated
 *   compositor layer, which read as jittery on the page. Holding the
 *   tiles still after the entrance keeps the composition calm.
 *   Respects `prefers-reduced-motion` via the page-wide MotionConfig.
 */
export function FloatingTile({
  position,
  delay = 0,
  children,
  debugLabel,
}: {
  position: { xPct: number; y: number }
  delay?: number
  children: ReactNode
  /**
   * Dev-only: when set, the tile becomes draggable and renders a tiny
   * coordinate pill below it showing the live `xPct%, yPx`. The
   * supplied string is used as a prefix (e.g. the icon name) so it's
   * easy to tell which tile is which when reading the screen back to
   * paste into source. Off in production — callers pass it only while
   * iterating; the label disappears the moment you drop the prop.
   */
  debugLabel?: string
}) {
  // Plain CSS transition rather than framer-motion: the page's LazyMotion
  // is wired with `domAnimation` (the lightweight feature set) which does
  // not include filter interpolation, so `blur(...)` would otherwise be
  // applied as a static initial value with no transition.
  const [shown, setShown] = useState(false)
  useEffect(() => {
    const t = window.setTimeout(() => setShown(true), delay * 1000)
    return () => window.clearTimeout(t)
  }, [delay])

  // Once the entrance transition has finished, drop `filter` from the
  // wrapper entirely. Critical for the data tiles' glass look: any
  // non-trivial filter on a parent creates an isolated rendering
  // context and silently breaks `backdrop-filter` on children. While
  // `shown=true` we still set `filter: blur(0px)` so the entrance can
  // smoothly transition; this effect waits one transition duration
  // and then clears `filter` so backdrop-blur kicks back in.
  const [entranceDone, setEntranceDone] = useState(false)
  useEffect(() => {
    if (!shown) return
    // 1000ms matches the `opacity/filter/transform 1s` transitions below.
    // Adding a small buffer (50ms) avoids racing the end of the animation.
    const t = window.setTimeout(() => setEntranceDone(true), 1050)
    return () => window.clearTimeout(t)
  }, [shown])

  // Live position state for the drag-to-tune affordance. Mirrors the
  // incoming `position` until the user starts dragging, then drifts
  // independently so the rendered coordinates match what you see.
  const [livePos, setLivePos] = useState(position)
  useEffect(() => {
    // Keep state in sync with prop changes (e.g. hot reload after
    // pasting new coordinates back into source).
    setLivePos(position)
    // Re-sync only when the meaningful coordinates change, not on every new
    // `position` object identity — depending on `position` itself would re-run
    // each parent render and risk a setState loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position.xPct, position.y])

  // Drag bookkeeping. Stored in refs because we only need the values
  // during the pointer-move handler — re-rendering on every cursor
  // movement is paid for by the setState that actually moves the tile.
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const dragRef = useRef<{
    pointerId: number
    startXPct: number
    startY: number
    startClientX: number
    startClientY: number
    parentWidth: number
  } | null>(null)

  const isDraggable = Boolean(debugLabel)

  const handlePointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (!isDraggable) return
      const parent = wrapperRef.current?.offsetParent as HTMLElement | null
      if (!parent) return
      const parentRect = parent.getBoundingClientRect()
      dragRef.current = {
        pointerId: e.pointerId,
        startXPct: livePos.xPct,
        startY: livePos.y,
        startClientX: e.clientX,
        startClientY: e.clientY,
        parentWidth: parentRect.width,
      }
      e.currentTarget.setPointerCapture(e.pointerId)
      e.preventDefault()
    },
    [isDraggable, livePos.xPct, livePos.y]
  )

  const handlePointerMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current
      if (!drag || drag.pointerId !== e.pointerId) return
      const dx = e.clientX - drag.startClientX
      const dy = e.clientY - drag.startClientY
      const nextXPct =
        drag.parentWidth > 0
          ? drag.startXPct + (dx / drag.parentWidth) * 100
          : drag.startXPct
      const nextY = drag.startY + dy
      setLivePos({
        xPct: Math.round(nextXPct * 100) / 100,
        y: Math.round(nextY),
      })
    },
    []
  )

  const handlePointerUp = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current
      if (!drag || drag.pointerId !== e.pointerId) return
      e.currentTarget.releasePointerCapture(e.pointerId)
      dragRef.current = null
    },
    []
  )

  return (
    <div
      ref={wrapperRef}
      className={
        isDraggable
          ? "pointer-events-auto absolute hidden cursor-grab touch-none select-none active:cursor-grabbing lg:block"
          : "pointer-events-none absolute hidden lg:block"
      }
      style={{
        left: `${livePos.xPct}%`,
        top: livePos.y,
        opacity: shown ? 1 : 0,
        // Three states: `blur(12px)` pre-entrance → `blur(0px)` during the
        // 1s ease → `undefined` once the entrance settles, so the
        // backdrop-blur on the tile children can refract the photo
        // behind them.
        filter: entranceDone ? undefined : shown ? "blur(0px)" : "blur(12px)",
        transform: shown ? "translateY(0)" : "translateY(16px)",
        // Suppress the transition during drag so the tile tracks the
        // cursor 1:1 instead of easing toward each new position.
        transition: dragRef.current
          ? undefined
          : `opacity 1s ${TILE_CSS_EASE}, filter 1s ${TILE_CSS_EASE}, transform 1s ${TILE_CSS_EASE}`,
        willChange: shown ? undefined : "filter, transform, opacity",
        // While in drag-tune mode, paint above any sibling tiles so the
        // user can grab the handle even where a data card would
        // otherwise cover it. Production tiles get no z-index and
        // stack by source order (platform tiles first → painted
        // behind data tiles, which is the desired final stacking).
        zIndex: isDraggable ? 50 : undefined,
      }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      {children}
      {debugLabel && (
        // Dev-only debug pill — sized off-system on purpose since it
        // never ships; using inline font-size keeps the typography
        // lint (which guards against arbitrary text-[Npx] utilities)
        // happy without an exemption entry.
        <span
          className="absolute top-full left-1/2 mt-1 -translate-x-1/2 rounded bg-black/70 px-1.5 py-0.5 font-mono leading-none whitespace-nowrap text-white"
          style={{ fontSize: 10 }}
          aria-hidden="true"
        >
          {debugLabel}: {livePos.xPct}%, {livePos.y}px
        </span>
      )}
    </div>
  )
}

/**
 * Bio age tile — Figma node 21:3871.
 *
 * Translucent glass card with the bio age number, a "X years younger"
 * caption, and a thin track-with-marker indicator. The marker is the
 * Figma "Union" shape (a pill with a circular cap on the left) rendered
 * inline so we don't have to ship an asset.
 *
 * Sizing matches Figma to the pixel where it matters (track 5×204,
 * marker offset 57px). Decimals from Figma rounded to clean values.
 */
export function BioAgeTile({
  label = "Biological age:",
  emphasis = "6 years younger",
}: {
  label?: string
  emphasis?: string
} = {}) {
  return (
    <div
      className="flex flex-col gap-2.5 rounded-[10px] bg-white/50 p-3 text-left backdrop-blur-[6px]"
      style={{ width: 230, border: "0.5px solid rgba(255, 255, 255, 0.4)" }}
    >
      <div className="flex flex-col leading-none">
        <div
          className="font-normal text-neutral-800"
          style={{
            fontSize: 38,
            lineHeight: "39px",
            letterSpacing: "-0.22px",
          }}
        >
          26
        </div>
        <p
          className="mt-1 text-neutral-900"
          style={{ fontSize: 11, lineHeight: "16px" }}
        >
          {label} <span className="font-medium">{emphasis}</span>
        </p>
      </div>

      {/* Track with marker — Union pill positioned at 57px from the left,
          matching Figma. The "6" sits over the circular cap on the left of
          the Union; the down-arrow sits to its right. */}
      <div className="relative h-[13px] w-[204px]">
        <div
          className="absolute top-1/2 h-[5px] w-full -translate-y-1/2 rounded-full"
          style={{ backgroundColor: "rgba(24, 68, 94, 0.308)" }} // ds-color-ignore: translucent overlay
        />
        <div
          className="absolute top-0 h-[13px]"
          style={{ left: 57, width: 98 }}
        >
          <svg
            viewBox="0 0 97.9531 13.0117"
            preserveAspectRatio="none"
            className="absolute inset-0 h-full w-full"
            aria-hidden="true"
          >
            <path
              d="M18.292 0C20.351 0 22.1863 0.957 23.3783 2.45C24.0236 3.258 24.9243 3.903 25.9586 3.903H95.3506C96.7879 3.903 97.9531 5.069 97.9531 6.506C97.9531 7.943 96.7878 9.108 95.3506 9.108H25.9586C24.9243 9.108 24.0237 9.754 23.3783 10.562C22.1864 12.055 20.3511 13.012 18.292 13.012H6.50586C2.91287 13.012 0 10.099 0 6.506C0.0000804 2.913 2.91292 0.0002 6.50586 0H18.292Z"
              fill="white"
            />
          </svg>
          <span
            className="absolute font-normal text-neutral-900"
            style={{ left: 4.5, top: 0, fontSize: 9, lineHeight: "13px" }}
          >
            6
          </span>
          <svg
            viewBox="0 0 6.506 4.96"
            className="absolute"
            style={{
              left: 13.5,
              top: 3.5,
              width: 4.96,
              height: 6.506,
              transform: "rotate(90deg)",
            }}
            aria-hidden="true"
          >
            <path
              d="M3.726 0.099C3.86 -0.033 4.079 -0.033 4.213 0.099L6.405 2.242C6.54 2.373 6.54 2.586 6.405 2.718L4.213 4.861C4.079 4.992 3.86 4.993 3.726 4.861C3.591 4.73 3.592 4.516 3.726 4.385L5.33 2.817H0V2.143H5.33L3.726 0.575C3.592 0.443 3.591 0.23 3.726 0.099Z"
              fill="#2D2D2D"
            />
          </svg>
        </div>
      </div>
    </div>
  )
}

/**
 * Biomarkers tile — Figma node 21:3851.
 *
 * Glass card with the biomarker count and a 3-segment color bar
 * (deep-green, light-green, terracotta) representing the breakdown of
 * optimal / in-range / out-of-range biomarkers.
 */
export function BiomarkersTile({
  label = "Biomarker",
}: {
  label?: string
} = {}) {
  return (
    <div
      className="flex flex-col gap-2.5 rounded-[10px] bg-white/50 p-3 text-left backdrop-blur-[6px]"
      style={{ width: 230, border: "0.5px solid rgba(255, 255, 255, 0.4)" }}
    >
      <div className="flex flex-col leading-none">
        <div
          className="font-normal text-neutral-800"
          style={{
            fontSize: 38,
            lineHeight: "39px",
            letterSpacing: "-0.22px",
          }}
        >
          81
        </div>
        <p
          className="mt-1 text-neutral-900"
          style={{ fontSize: 11, lineHeight: "16px" }}
        >
          {label}
        </p>
      </div>
      <div className="flex h-[5px] w-full items-center gap-[3px]">
        <div
          className="h-full rounded-full"
          style={{ width: 22, backgroundColor: "#408c49" }}
        />
        <div
          className="h-full flex-1 rounded-full"
          style={{ backgroundColor: "#95a73b" }}
        />
        <div
          className="h-full rounded-full"
          style={{ width: 12, backgroundColor: "#d25a31" }}
        />
      </div>
    </div>
  )
}

/**
 * Action plan tile — Figma nodes 21:706 / 21:715 / 21:724.
 *
 * Thin horizontal pill with a leading icon and a two-line label
 * (title + description). Designed to be reused for each action — pass
 * the icon as children, set the title/description.
 *
 * `iconRotation` is for the potassium tile, where the test-tube icon
 * tilts -15.88° per Figma.
 */
export function ActionPlanTile({
  icon,
  iconRotation = 0,
  iconSize = 20,
  title,
  description,
}: {
  icon: ReactNode
  iconRotation?: number
  iconSize?: number
  title: string
  description: string
}) {
  return (
    <div
      className="flex items-center gap-3 rounded-[10px] bg-white/50 p-3 text-left backdrop-blur-[6px]"
      style={{
        width: 240,
        border: "0.5px solid rgba(255, 255, 255, 0.4)",
      }}
    >
      <div className="flex size-8 shrink-0 items-center justify-center">
        <div
          className="flex items-center justify-center"
          style={{
            width: iconSize,
            height: iconSize,
            transform: iconRotation ? `rotate(${iconRotation}deg)` : undefined,
          }}
        >
          {icon}
        </div>
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-[2.5px]">
        <p
          className="truncate font-normal text-neutral-900"
          style={{ fontSize: 14, lineHeight: "18px", letterSpacing: "-0.23px" }}
        >
          {title}
        </p>
        <p
          className="font-normal text-neutral-900"
          style={{ fontSize: 10, lineHeight: "13px" }}
        >
          {description}
        </p>
      </div>
    </div>
  )
}
/** Dumbbell icon — Figma 21:717. */
export function DumbbellIcon({ size = 21 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size * (13.29 / 18.51)}
      viewBox="0 0 18.5057 13.2928"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M4.4936 0.0649C5.1908 0.0651 5.7482 0.6846 5.7485 1.4331V4.4096H12.7573V1.4331C12.7575 0.6846 13.315 0.0651 14.0122 0.0649H14.8061C15.5032 0.0651 16.0607 0.6846 16.061 1.4331V2.7241C16.1655 2.6913 16.2762 2.6714 16.392 2.6713H17.185C17.8823 2.6713 18.4406 3.2909 18.4409 4.0395V9.2524C18.4409 10.0012 17.8825 10.6215 17.185 10.6215H16.392C16.2761 10.6215 16.1656 10.6006 16.061 10.5678V11.8588C16.061 12.6075 15.5034 13.2277 14.8061 13.228H14.0122C13.3148 13.2278 12.7573 12.6075 12.7573 11.8588V8.8832H5.7485V11.8588C5.7485 12.6075 5.1909 13.2278 4.4936 13.228H3.6997C3.0023 13.2278 2.4448 12.6075 2.4448 11.8588V10.5678C2.3402 10.6006 2.2297 10.6215 2.1137 10.6215H1.3198C0.6225 10.6213 0.0649 10.001 0.0649 9.2524V4.0395C0.0651 3.2911 0.6227 2.6716 1.3198 2.6713H2.1137C2.2296 2.6714 2.3403 2.6913 2.4448 2.7241V1.4331C2.4450 0.6846 3.0025 0.0651 3.6997 0.0649H4.4936ZM3.6997 1.0639C3.5228 1.0641 3.3688 1.2243 3.3686 1.4331V11.8588C3.3686 12.0678 3.5227 12.2287 3.6997 12.229H4.4936C4.6706 12.2288 4.8247 12.0678 4.8247 11.8588V1.4331C4.8244 1.2243 4.6704 1.0641 4.4936 1.0639H3.6997ZM14.0122 1.0639C13.8353 1.0641 13.6813 1.2243 13.6811 1.4331V11.8588C13.6811 12.0678 13.8352 12.2288 14.0122 12.229H14.8061C14.983 12.2287 15.1372 12.0677 15.1372 11.8588V1.4331C15.1369 1.2244 14.9829 1.0642 14.8061 1.0639H14.0122ZM1.3198 3.6704C1.143 3.6706 0.989 3.8308 0.9887 4.0395V9.2524C0.9887 9.4613 1.1429 9.6222 1.3198 9.6225H2.1137C2.2907 9.6224 2.4448 9.4614 2.4448 9.2524V4.0395C2.4445 3.8308 2.2906 3.6705 2.1137 3.6704H1.3198ZM16.392 3.6704C16.2151 3.6705 16.0612 3.8307 16.061 4.0395V9.2524C16.061 9.4614 16.215 9.6224 16.392 9.6225H17.185C17.3621 9.6225 17.517 9.4615 17.517 9.2524V4.0395C17.5168 3.8307 17.362 3.6704 17.185 3.6704H16.392ZM5.7485 7.8842H12.7573V5.4086H5.7485V7.8842Z"
        fill="#2D2D2D"
        stroke="#2D2D2D"
        strokeWidth="0.13"
      />
    </svg>
  )
}

/** Test tube icon — Figma 21:726. (Tile rotates it -15.88° in the wrapper.) */
export function TestTubeIcon({ size = 19 }: { size?: number }) {
  return (
    <svg
      width={size * (6.22 / 18.65)}
      height={size}
      viewBox="0 0 6.21779 18.6534"
      fill="none"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M5.7937 2.2422H5.6787V16.0041C5.6788 17.4649 4.5258 18.6534 3.1088 18.6534C1.6917 18.6534 0.5388 17.4649 0.5388 16.0041L0.5389 2.2422H0.4241C0.1898 2.2422 0 2.0466 0 1.8051V0.4372C0 0.1956 0.1898 0 0.4241 0H5.7937C6.028 0 6.2178 0.1956 6.2178 0.4372V1.8051C6.2178 2.0466 6.0279 2.2422 5.7937 2.2422ZM4.8305 2.2422H1.387V8.7768C2.7687 8.5307 3.3688 9.0178 3.6292 9.5029C3.8219 9.8614 4.2255 10.0032 4.8305 9.9261L4.8305 2.2422ZM1.387 9.6666V16.004C1.387 16.9827 2.1594 17.7789 3.1087 17.7789C4.0582 17.7789 4.8305 16.9827 4.8305 16.004L4.8305 10.8057C3.8324 10.9036 3.2116 10.5305 2.8872 9.9265C2.6566 9.497 1.9424 9.5488 1.387 9.6666ZM0.848 1.3678H5.3696V0.8743H0.848V1.3678Z"
        fill="#1F1F1F"
      />
    </svg>
  )
}
